import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DECISIONS = ["renew", "renegotiate", "terminate", "let_lapse"];
const CLASSIFICATIONS = ["Public", "Internal", "Confidential", "Restricted"];
const RENEWAL_TYPES = ["renew_by_decision", "auto_renew", "terminating"];
const CREDIT_BASES = ["", "attainment_percent", "per_breach", "both"];
const EMPTY_INTAKE = {
  vendor_key: "", title: "", source_reference: "", term_start: "", term_end: "",
  notice_period_days: "60", renewal_type: "renew_by_decision", owner_email: "",
  annual_value: "", covered_service_keys: [], data_classification: "Confidential",
  credit_basis: "", attainment_threshold_pct: "95", credit_pct_of_monthly_fee: "5",
  per_breach_amount: "500",
};

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [digest, setDigest] = useState(null);
  const [credits, setCredits] = useState(null);
  const [spend, setSpend] = useState(null);
  const [intake, setIntake] = useState(EMPTY_INTAKE);
  const [services, setServices] = useState([]);
  const [doc, setDoc] = useState(null);
  const [claim, setClaim] = useState({ period: "", note: "" });
  const [canWrite, setCanWrite] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [quarterOnly, setQuarterOnly] = useState(false);
  const [obligations, setObligations] = useState([]);
  const [vault, setVault] = useState(null);
  const [obForm, setObForm] = useState({ title: "", kind: "deliverable", due_date: "",
                                          owner_email: "", acceptance_criteria: "" });
  const [docForm, setDocForm] = useState({ kind: "amendment", effective_date: "", file: null });
  const [calendar, setCalendar] = useState(null);
  const [window_, setWindow] = useState("12_months");
  const [compare, setCompare] = useState({ a: "", b: "", result: null });
  const [reminders, setReminders] = useState(null);
  const [renewalDigest, setRenewalDigest] = useState(null);
  const [form, setForm] = useState({ decision: "renew", justification: "" });
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    apiFetch("/contracts").then(async (r) => {
      if (r.status === 403) { setError("You do not have contract access (contract.read)."); return; }
      setContracts(await r.json());
    });
    apiFetch("/contracts/renewal-alerts").then((r) => (r.ok ? r.json() : null)).then(setAlerts);
    apiFetch("/digests/renewal").then((r) => (r.ok ? r.json() : null)).then(setRenewalDigest);
    apiFetch("/contracts/governance-exceptions").then((r) => (r.ok ? r.json() : { exceptions: [] }))
      .then((d) => setExceptions(d.exceptions));
    apiFetch("/vendors").then((r) => (r.ok ? r.json() : [])).then(setVendors);
    apiFetch("/catalog").then((r) => (r.ok ? r.json() : { services: [] })).then((d) => {
      const flat = [];
      (d.services || []).forEach((s) => {
        flat.push({ key: s.key, name: s.name });
        (s.offerings || []).forEach((o) => flat.push({ key: o.key, name: `${s.name} › ${o.name}` }));
      });
      setServices(flat);
    });
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/auth/my-permissions").then((r) => (r.ok ? r.json() : { permissions: [] }))
      .then((d) => setCanWrite(d.permissions.includes("contract.write")));
    refresh();
  }, [navigate, refresh]);

  const post = (path, body, label) => {
    setStatus(`${label} in progress…`);
    return apiFetch(path, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
      const data = await r.json();
      setStatus(r.ok ? `${label} done` : `${label} failed — ${JSON.stringify(data.detail)}`);
      if (r.ok) refresh();
      return data;
    });
  };

  const submitIntake = async () => {
    setStatus("Recording contract…");
    const payload = {
      vendor_key: intake.vendor_key, title: intake.title,
      source_reference: intake.source_reference, term_start: intake.term_start,
      term_end: intake.term_end, owner_email: intake.owner_email,
      notice_period_days: Number(intake.notice_period_days || 0),
      renewal_type: intake.renewal_type,
      annual_value: intake.annual_value === "" ? null : Number(intake.annual_value),
      covered_service_keys: intake.covered_service_keys,
      data_classification: intake.data_classification,
    };
    if (intake.credit_basis) {
      payload.credit_schedule = {
        basis: intake.credit_basis,
        attainment_threshold_pct: Number(intake.attainment_threshold_pct),
        credit_pct_of_monthly_fee: Number(intake.credit_pct_of_monthly_fee),
        per_breach_amount: Number(intake.per_breach_amount),
      };
    }
    const r = await apiFetch("/contracts", { method: "POST", body: JSON.stringify(payload) });
    const data = await r.json();
    if (!r.ok) { setStatus(`Contract not recorded — ${JSON.stringify(data.detail)}`); return; }
    if (doc) {
      const form = new FormData();
      form.append("file", doc);
      form.append("parent_type", "contract");
      form.append("parent_id", data.id);
      const up = await apiFetch("/files/upload", { method: "POST", body: form });
      setStatus(up.ok
        ? `${data.number} recorded with the signed document attached`
        : `${data.number} recorded, but the document upload failed`);
    } else {
      setStatus(`${data.number} recorded`);
    }
    setIntake(EMPTY_INTAKE);
    setDoc(null);
    refresh();
  };

  const openCredits = (contract) =>
    apiFetch(`/contracts/${contract.id}/credits`)
      .then((r) => (r.ok ? r.json() : null)).then(setCredits);

  const openDetail = (contract) => {
    setSelected(contract);
    setScorecard(null);
    apiFetch(`/contracts/${contract.id}/obligations`)
      .then((r) => (r.ok ? r.json() : [])).then(setObligations);
    apiFetch(`/contracts/${contract.id}/documents`)
      .then((r) => (r.ok ? r.json() : null)).then(setVault);
  };

  const reloadDetail = (contractId) => {
    apiFetch(`/contracts/${contractId}/obligations`)
      .then((r) => (r.ok ? r.json() : [])).then(setObligations);
    apiFetch(`/contracts/${contractId}/documents`)
      .then((r) => (r.ok ? r.json() : null)).then(setVault);
  };

  const addObligation = () =>
    post(`/contracts/${selected.id}/obligations`, {
      title: obForm.title, kind: obForm.kind, due_date: obForm.due_date,
      owner_email: obForm.owner_email, acceptance_criteria: obForm.acceptance_criteria,
    }, "Obligation").then((d) => {
      if (d.id) {
        setObForm({ title: "", kind: "deliverable", due_date: "", owner_email: "",
                     acceptance_criteria: "" });
        reloadDetail(selected.id);
      }
    });

  const uploadDocument = async () => {
    if (!docForm.file) { setStatus("Choose a document first"); return; }
    setStatus("Uploading document…");
    const form = new FormData();
    form.append("file", docForm.file);
    form.append("kind", docForm.kind);
    form.append("effective_date", docForm.effective_date);
    const r = await apiFetch(`/contracts/${selected.id}/documents`,
                             { method: "POST", body: form });
    const body = await r.json();
    setStatus(r.ok ? `${docForm.kind.replace(/_/g, " ")} added to the vault`
                   : `Upload failed — ${JSON.stringify(body.detail)}`);
    if (r.ok) { setDocForm({ kind: "amendment", effective_date: "", file: null });
                reloadDetail(selected.id); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="contracts-title">
            Contracts, Renewals and Vendor Performance
          </h1>
          <p className="text-sm text-muted-foreground">
            HCD Legal stays the system of record for the document; the platform makes sure no
            renewal happens by accident.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="contracts-rebid-link">
            <Link to="/rebid">Rebid workspace</Link>
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="contracts-problems-link">
            <Link to="/problems">Problems</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="contracts-error">{error}</p>}
        {status && <p className="text-sm text-amber-800" data-testid="contracts-status">{status}</p>}

        {exceptions.length > 0 && (
          <section className="border border-red-300 rounded-md p-4 space-y-1"
                   data-testid="contracts-exceptions">
            <h2 className="text-lg font-medium text-red-800">Governance exceptions</h2>
            {exceptions.map((e) => (
              <p key={e.contract_id} className="text-sm" data-testid={`exception-${e.number}`}>
                {e.number} · {e.vendor_name} · notice deadline {e.notice_deadline} passed with no
                decision ({e.renewal_type.replace(/_/g, " ")}) · owner {e.owner_email}
              </p>
            ))}
          </section>
        )}

        {canWrite && (
        <section className="space-y-3 border rounded-md p-4" data-testid="contract-intake">
          <h2 className="text-lg font-medium">Add a contract</h2>
          <p className="text-sm text-muted-foreground">
            HCD Legal holds the agreement. Record its reference and dates here so the platform
            can warn the owner before the notice deadline.
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            <select className="border rounded-md px-2 py-2 text-sm bg-background"
                    aria-label="Vendor" data-testid="intake-vendor"
                    value={intake.vendor_key}
                    onChange={(e) => setIntake({ ...intake, vendor_key: e.target.value })}>
              <option value="">Select a vendor…</option>
              {[...vendors].sort((a, b) => a.name.localeCompare(b.name)).map((v) => (
                <option key={v.vendor_key} value={v.vendor_key}>{v.name}</option>
              ))}
            </select>
            <Input placeholder="Title" value={intake.title} data-testid="intake-title"
                   onChange={(e) => setIntake({ ...intake, title: e.target.value })} />
            <Input placeholder="Legal reference (e.g. LEGAL-CLM-2026-0042)"
                   value={intake.source_reference} data-testid="intake-reference"
                   onChange={(e) => setIntake({ ...intake, source_reference: e.target.value })} />
            <Input placeholder="Owner email" value={intake.owner_email}
                   data-testid="intake-owner"
                   onChange={(e) => setIntake({ ...intake, owner_email: e.target.value })} />
            <label className="text-sm">Term start
              <Input type="date" value={intake.term_start} data-testid="intake-term-start"
                     onChange={(e) => setIntake({ ...intake, term_start: e.target.value })} />
            </label>
            <label className="text-sm">Term end
              <Input type="date" value={intake.term_end} data-testid="intake-term-end"
                     onChange={(e) => setIntake({ ...intake, term_end: e.target.value })} />
            </label>
            <label className="text-sm">Notice period (days)
              <Input type="number" min="0" value={intake.notice_period_days}
                     data-testid="intake-notice"
                     onChange={(e) => setIntake({ ...intake, notice_period_days: e.target.value })} />
            </label>
            <label className="text-sm">Renewal type
              <select className="w-full border rounded-md px-2 py-2 text-sm bg-background"
                      data-testid="intake-renewal-type" value={intake.renewal_type}
                      onChange={(e) => setIntake({ ...intake, renewal_type: e.target.value })}>
                {RENEWAL_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="text-sm">Annual value (non-authoritative copy)
              <Input type="number" min="0" value={intake.annual_value}
                     data-testid="intake-value"
                     onChange={(e) => setIntake({ ...intake, annual_value: e.target.value })} />
            </label>
            <label className="text-sm">Data classification
              <select className="w-full border rounded-md px-2 py-2 text-sm bg-background"
                      data-testid="intake-classification" value={intake.data_classification}
                      onChange={(e) => setIntake({ ...intake, data_classification: e.target.value })}>
                {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <fieldset>
            <legend className="text-sm font-medium">Covered catalog items</legend>
            <div className="flex flex-wrap gap-3 mt-1 max-h-32 overflow-y-auto">
              {services.map((s) => (
                <label key={s.key} className="text-sm flex items-center gap-1">
                  <input type="checkbox" data-testid={`intake-service-${s.key}`}
                         checked={intake.covered_service_keys.includes(s.key)}
                         onChange={() => setIntake({
                           ...intake,
                           covered_service_keys: intake.covered_service_keys.includes(s.key)
                             ? intake.covered_service_keys.filter((k) => k !== s.key)
                             : [...intake.covered_service_keys, s.key],
                         })} />
                  {s.name}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-2 md:grid-cols-4">
            <label className="text-sm">Service credits
              <select className="w-full border rounded-md px-2 py-2 text-sm bg-background"
                      data-testid="intake-credit-basis" value={intake.credit_basis}
                      onChange={(e) => setIntake({ ...intake, credit_basis: e.target.value })}>
                {CREDIT_BASES.map((b) => (
                  <option key={b} value={b}>{b ? b.replace(/_/g, " ") : "no credit terms"}</option>
                ))}
              </select>
            </label>
            {(intake.credit_basis === "attainment_percent" || intake.credit_basis === "both") && (
              <>
                <label className="text-sm">Attainment threshold %
                  <Input type="number" value={intake.attainment_threshold_pct}
                         data-testid="intake-credit-threshold"
                         onChange={(e) => setIntake({ ...intake, attainment_threshold_pct: e.target.value })} />
                </label>
                <label className="text-sm">Credit % of monthly fee
                  <Input type="number" value={intake.credit_pct_of_monthly_fee}
                         data-testid="intake-credit-pct"
                         onChange={(e) => setIntake({ ...intake, credit_pct_of_monthly_fee: e.target.value })} />
                </label>
              </>
            )}
            {(intake.credit_basis === "per_breach" || intake.credit_basis === "both") && (
              <label className="text-sm">Amount per breach
                <Input type="number" value={intake.per_breach_amount}
                       data-testid="intake-credit-per-breach"
                       onChange={(e) => setIntake({ ...intake, per_breach_amount: e.target.value })} />
              </label>
            )}
          </div>
          <label className="text-sm block">Signed document (optional)
            <input type="file" data-testid="intake-document"
                   aria-label="Signed agreement file"
                   className="block text-sm mt-1"
                   onChange={(e) => setDoc(e.target.files?.[0] || null)} />
          </label>
          <Button size="sm" data-testid="intake-submit" onClick={submitIntake}>
            Record contract
          </Button>
        </section>
        )}

        {renewalDigest && (
          <section className="space-y-2" data-testid="renewal-digest">
            <h2 className="text-lg font-medium">Monthly renewal digest</h2>
            <p className="text-sm text-muted-foreground" data-testid="renewal-digest-schedule">
              {renewalDigest.definition} {renewalDigest.schedule}
            </p>
            <p className="text-sm" data-testid="renewal-digest-totals">
              {renewalDigest.totals.events} decision point(s) across{" "}
              {renewalDigest.totals.contracts} contract(s) and {renewalDigest.totals.owners}{" "}
              owner(s) due by {renewalDigest.horizon} · {renewalDigest.totals.undecided} with no
              decision on file
            </p>
            <ul className="text-sm space-y-1" data-testid="renewal-digest-rows">
              {renewalDigest.rows.map((row) => (
                <li key={`${row.number}-${row.event}`}
                    data-testid={`renewal-digest-row-${row.number}-${row.event.replace(/\s/g, "-")}`}>
                  <span className="font-mono">{row.number}</span> {row.vendor_name} —{" "}
                  {row.event} {row.date} ({row.days_away} days), owner {row.owner_email},{" "}
                  {row.decision_on_file
                    ? `decision on file: ${row.decision_on_file}`
                    : "no decision on file yet"}
                </li>
              ))}
              {renewalDigest.rows.length === 0 && (
                <li className="text-muted-foreground" data-testid="renewal-digest-empty">
                  Nothing falls due in the next quarter.
                </li>
              )}
            </ul>
            <details className="text-sm" data-testid="renewal-digest-preview">
              <summary className="cursor-pointer">Who receives it, and what they will read</summary>
              <ul className="mt-2 space-y-2">
                {renewalDigest.deliveries.map((d, i) => (
                  <li key={`${d.recipient}-${i}`} className="border rounded-md p-2"
                      data-testid={`renewal-digest-delivery-${i}`}>
                    <p className="font-medium">
                      {d.recipient} — {d.scope === "rollup" ? "full roll-up" : "own contracts"}
                    </p>
                    <p className="text-xs text-muted-foreground">{d.subject}</p>
                    <pre className="whitespace-pre-wrap text-xs mt-1">{d.rendered.join("\n")}</pre>
                  </li>
                ))}
              </ul>
            </details>
            {canWrite && (
              <Button size="sm" variant="outline" data-testid="renewal-digest-send"
                      onClick={() => post("/digests/renewal/send", {}, "Renewal digest sent")}>
                Send it now
              </Button>
            )}
          </section>
        )}

        <section className="space-y-3" data-testid="contracts-list">
          <h2 className="text-lg font-medium">Contracts</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border rounded-md">
              <caption className="sr-only">Contracts with term, owner and renewal status</caption>
              <thead>
                <tr className="text-left border-b">
                  <th scope="col" className="px-3 py-2">Contract</th>
                  <th scope="col" className="px-3 py-2">Vendor</th>
                  <th scope="col" className="px-3 py-2">Term end</th>
                  <th scope="col" className="px-3 py-2">Notice deadline</th>
                  <th scope="col" className="px-3 py-2">Renewal</th>
                  <th scope="col" className="px-3 py-2">Annual value</th>
                  <th scope="col" className="px-3 py-2">Decision</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} className="border-b last:border-0"
                      data-testid={`contract-row-${c.number}`}>
                    <th scope="row" className="px-3 py-2 text-left font-medium">
                      <button className="underline" data-testid={`contract-open-${c.number}`}
                              onClick={() => openDetail(c)}>
                        {c.number}
                      </button>
                      <span className="block text-muted-foreground font-normal">{c.title}</span>
                    </th>
                    <td className="px-3 py-2">{c.vendor_name}</td>
                    <td className="px-3 py-2">{c.term_end}</td>
                    <td className="px-3 py-2">{c.renewal.notice_deadline || "—"}</td>
                    <td className="px-3 py-2">
                      {c.renewal.state.replace(/_/g, " ")}
                      {c.renewal.days_to_notice !== null && (
                        <span className="text-muted-foreground"> ({c.renewal.days_to_notice}d)</span>
                      )}
                    </td>
                    <td className="px-3 py-2" data-testid={`contract-value-${c.number}`}>
                      {c.annual_value !== undefined && c.annual_value !== null
                        ? `${c.currency} ${c.annual_value.toLocaleString()}`
                        : c.financials_withheld ? "restricted" : "not recorded"}
                    </td>
                    <td className="px-3 py-2">
                      {c.decision ? c.decision.decision.replace(/_/g, " ") : "none on file"}
                      <Button size="sm" variant="ghost" data-testid={`contract-credits-${c.number}`}
                              onClick={() => openCredits(c)}>Credits</Button>
                    </td>
                  </tr>
                ))}
                {contracts.length === 0 && (
                  <tr><td colSpan="7" className="px-3 py-2 text-muted-foreground"
                          data-testid="contracts-empty">No contracts recorded.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {selected && (
          <section className="border rounded-md p-4 space-y-3" data-testid="contract-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium" data-testid="contract-detail-title">
                {selected.number} — {selected.title}
              </h2>
              <Button size="sm" variant="ghost" data-testid="contract-detail-close"
                      onClick={() => setSelected(null)}>Close</Button>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="contract-authority">
              {selected.authority}
            </p>
            <p className="text-sm">
              Authoritative document: {selected.source_reference} · owner {selected.owner_email} ·
              renewal type {selected.renewal_type.replace(/_/g, " ")}
            </p>
            {selected.decision ? (
              <p className="text-sm" data-testid="contract-decision">
                Decision on file: {selected.decision.decision.replace(/_/g, " ")} —{" "}
                {selected.decision.justification} (by {selected.decision.by_email})
              </p>
            ) : (
              <div className="space-y-2">
                <select className="border rounded-md px-2 py-1 text-sm bg-background"
                        aria-label="Renewal decision" data-testid="contract-decision-select"
                        value={form.decision}
                        onChange={(e) => setForm({ ...form, decision: e.target.value })}>
                  {DECISIONS.map((d) => <option key={d} value={d}>{d.replace(/_/g, " ")}</option>)}
                </select>
                <Input placeholder="Justification (required)" value={form.justification}
                       data-testid="contract-justification-input"
                       onChange={(e) => setForm({ ...form, justification: e.target.value })} />
                <Button size="sm" variant="outline" data-testid="contract-decision-save"
                        onClick={() => post(`/contracts/${selected.id}/decision`,
                                             { decision: form.decision,
                                               justification: form.justification },
                                             "Renewal decision")
                          .then(() => setSelected(null))}>
                  Record renewal decision
                </Button>
              </div>
            )}

            <section className="space-y-3" data-testid="obligations-block">
              <h3 className="text-sm font-semibold">Obligations — what the vendor promised</h3>
              <ul className="text-sm space-y-1" data-testid="obligations-list">
                {obligations.map((o) => (
                  <li key={o.id} className="border rounded px-3 py-2"
                      data-testid={`obligation-${o.id}`}>
                    <span className="font-medium">{o.kind}: {o.title}</span> · due {o.due_date} ·{" "}
                    owner {o.owner_email} ·{" "}
                    <span data-testid={`obligation-state-${o.id}`}>{o.state}</span>
                    {o.outcome && (
                      <span className="text-muted-foreground"> — {o.outcome.outcome_note}</span>
                    )}
                    {canWrite && !o.outcome && (
                      <span className="ml-2 inline-flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`obligation-met-${o.id}`}
                                onClick={() => post(
                                  `/contracts/${selected.id}/obligations/${o.id}/outcome`,
                                  { outcome: "met", note: "accepted" }, "Obligation met")
                                  .then(() => reloadDetail(selected.id))}>Met</Button>
                        <Button size="sm" variant="ghost" data-testid={`obligation-missed-${o.id}`}
                                onClick={() => post(
                                  `/contracts/${selected.id}/obligations/${o.id}/outcome`,
                                  { outcome: "missed",
                                    note: "not delivered by the due date" }, "Obligation missed")
                                  .then(() => reloadDetail(selected.id))}>Missed</Button>
                      </span>
                    )}
                    {o.actions.length > 0 && (
                      <ul className="text-xs text-muted-foreground mt-1"
                          data-testid={`obligation-actions-${o.id}`}>
                        {o.actions.map((a) => (
                          <li key={a.id}>
                            Corrective action: {a.title} · due {a.due_date} · {a.status}
                            {canWrite && a.status === "open" && (
                              <Button size="sm" variant="ghost"
                                      data-testid={`obligation-action-close-${a.id}`}
                                      onClick={() => post(
                                        `/contracts/${selected.id}/obligation-actions/${a.id}/close`,
                                        { note: "chased and resolved" }, "Action")
                                        .then(() => reloadDetail(selected.id))}>Close</Button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
                {obligations.length === 0 && (
                  <li className="text-muted-foreground" data-testid="obligations-empty">
                    Nothing recorded yet — a contract that promises deliverables should list them.
                  </li>
                )}
              </ul>
              {canWrite && (
                <div className="grid gap-2 md:grid-cols-5" data-testid="obligation-form">
                  <Input placeholder="What was promised" value={obForm.title}
                         data-testid="obligation-title"
                         onChange={(e) => setObForm({ ...obForm, title: e.target.value })} />
                  <select className="border rounded-md px-2 py-2 text-sm bg-background"
                          aria-label="Obligation kind" data-testid="obligation-kind"
                          value={obForm.kind}
                          onChange={(e) => setObForm({ ...obForm, kind: e.target.value })}>
                    {["deliverable", "milestone", "report", "review"].map((k) => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                  <Input type="date" value={obForm.due_date} data-testid="obligation-due"
                         aria-label="Obligation due date"
                         onChange={(e) => setObForm({ ...obForm, due_date: e.target.value })} />
                  <Input placeholder="Owner email" value={obForm.owner_email}
                         data-testid="obligation-owner"
                         onChange={(e) => setObForm({ ...obForm, owner_email: e.target.value })} />
                  <Button size="sm" variant="outline" data-testid="obligation-add"
                          onClick={addObligation}>Add obligation</Button>
                </div>
              )}
            </section>

            {vault && (
              <section className="space-y-2" data-testid="document-vault">
                <h3 className="text-sm font-semibold">Document vault</h3>
                <p className="text-xs text-muted-foreground" data-testid="vault-authority">
                  {vault.authority_note}
                </p>
                {vault.gap && (
                  <p className="text-sm font-medium text-red-800 dark:text-red-300"
                     data-testid="vault-baa-gap">
                    {vault.gap}
                  </p>
                )}
                <ul className="text-sm space-y-1" data-testid="vault-list">
                  {vault.documents.map((d) => (
                    <li key={d.id} data-testid={`vault-doc-${d.id}`}>
                      {d.document_kind.replace(/_/g, " ")}: {d.filename}
                      {d.effective_date && ` · effective ${d.effective_date}`} ·{" "}
                      {Math.max(1, Math.round(d.size / 1024))} KB
                      <Button size="sm" variant="ghost" data-testid={`vault-download-${d.id}`}
                              onClick={async () => {
                                const r = await apiFetch(`/files/${d.id}/download`);
                                if (!r.ok) { setStatus("Download not permitted"); return; }
                                window.open(URL.createObjectURL(await r.blob()), "_blank");
                              }}>Download</Button>
                    </li>
                  ))}
                  {vault.documents.length === 0 && (
                    <li className="text-muted-foreground" data-testid="vault-empty">
                      No documents on file.
                    </li>
                  )}
                </ul>
                {canWrite && (
                  <div className="grid gap-2 md:grid-cols-4" data-testid="vault-form">
                    <select className="border rounded-md px-2 py-2 text-sm bg-background"
                            aria-label="Document kind" data-testid="vault-kind"
                            value={docForm.kind}
                            onChange={(e) => setDocForm({ ...docForm, kind: e.target.value })}>
                      {["signed_agreement", "amendment", "baa", "sow", "insurance", "other"]
                        .map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
                    </select>
                    <Input type="date" value={docForm.effective_date}
                           data-testid="vault-effective-date"
                           aria-label="Document effective date"
                           onChange={(e) => setDocForm({ ...docForm,
                                                          effective_date: e.target.value })} />
                    <input type="file" data-testid="vault-file" className="text-sm"
                           aria-label="Document file to add to the vault"
                           onChange={(e) => setDocForm({ ...docForm,
                                                          file: e.target.files?.[0] || null })} />
                    <Button size="sm" variant="outline" data-testid="vault-upload"
                            onClick={uploadDocument}>Add to vault</Button>
                  </div>
                )}
              </section>
            )}

          </section>
        )}

        {credits && (
          <section className="space-y-3 border rounded-md p-4" data-testid="credits-panel">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium" data-testid="credits-title">
                Service credits — {credits.number} · {credits.vendor_name}
              </h2>
              <Button size="sm" variant="ghost" data-testid="credits-close"
                      onClick={() => setCredits(null)}>Close</Button>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="credits-method">
              {credits.definition} Method: {credits.method}. These figures are not
              authoritative — {credits.authority_note}
            </p>
            {credits.no_schedule_note ? (
              <p className="text-sm text-amber-800" data-testid="credits-no-schedule">
                {credits.no_schedule_note}
              </p>
            ) : (
              <p className="text-sm" data-testid="credits-summary">
                Basis: {credits.credit_schedule.basis.replace(/_/g, " ")} · monthly fee basis{" "}
                {credits.currency} {credits.monthly_fee_basis} · unclaimed{" "}
                {credits.currency} {credits.credit_owed_unclaimed} · recovered{" "}
                {credits.currency} {credits.credit_recovered}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md">
                <caption className="sr-only">Monthly attainment and credits owed</caption>
                <thead>
                  <tr className="text-left border-b">
                    <th scope="col" className="px-3 py-2">Month</th>
                    <th scope="col" className="px-3 py-2">Measured</th>
                    <th scope="col" className="px-3 py-2">Attainment</th>
                    <th scope="col" className="px-3 py-2">Breaches</th>
                    <th scope="col" className="px-3 py-2">Credit due</th>
                    <th scope="col" className="px-3 py-2">Claim</th>
                  </tr>
                </thead>
                <tbody>
                  {credits.periods.map((p) => (
                    <tr key={p.period} className="border-b last:border-0"
                        data-testid={`credit-row-${p.period}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium">{p.period}</th>
                      <td className="px-3 py-2">{p.measured}</td>
                      <td className="px-3 py-2">{p.attainment_pct}%</td>
                      <td className="px-3 py-2">{p.breaches}</td>
                      <td className="px-3 py-2">
                        {p.credit_due ? `${credits.currency} ${p.credit_due}` : "—"}
                        {p.credit_reason && (
                          <span className="block text-xs text-muted-foreground">
                            {p.credit_reason}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {p.claim ? (
                          <span data-testid={`claim-status-${p.period}`}>
                            {p.claim.status} · {credits.currency} {p.claim.amount}
                          </span>
                        ) : p.credit_due ? (
                          <Button size="sm" variant="outline"
                                  data-testid={`claim-record-${p.period}`}
                                  onClick={() => post(
                                    `/contracts/${credits.contract_id}/credits/claims`,
                                    { period: p.period, status: "claimed",
                                      note: "claimed at renewal review" }, "Credit claim")
                                    .then((d) => d.periods && setCredits(d))}>
                            Mark claimed
                          </Button>
                        ) : "—"}
                        {p.claim && p.claim.status === "claimed" && (
                          <Button size="sm" variant="ghost"
                                  data-testid={`claim-recover-${p.period}`}
                                  onClick={() => post(
                                    `/contracts/${credits.contract_id}/credits/claims`,
                                    { period: p.period, status: "recovered",
                                      note: "credited on the next invoice" }, "Credit recovery")
                                    .then((d) => d.periods && setCredits(d))}>
                            Mark recovered
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {credits.periods.length === 0 && (
                    <tr><td colSpan="6" className="px-3 py-2 text-muted-foreground"
                            data-testid="credits-empty">
                      No closed work has been measured against this contract's catalog items yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="space-y-2" data-testid="renewal-calendar">
          <h2 className="text-lg font-medium">Renewal calendar</h2>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm">Window
              <select className="block border rounded-md px-2 py-2 text-sm bg-background"
                      data-testid="calendar-window" value={window_}
                      onChange={(e) => setWindow(e.target.value)}>
                <option value="12_months">Next 12 months</option>
                <option value="2_quarters">Next 2 quarters</option>
                <option value="all">Everything recorded</option>
              </select>
            </label>
            <Button size="sm" variant="outline" data-testid="calendar-load"
                    onClick={() => apiFetch(`/contracts/renewal-calendar?window=${window_}`)
                      .then((r) => (r.ok ? r.json() : null)).then(setCalendar)}>
              Show calendar
            </Button>
          </div>
          {calendar && (
            <div className="border rounded-md p-4 space-y-3 text-sm" data-testid="calendar-body">
              <p className="text-neutral-700 dark:text-neutral-300" data-testid="calendar-method">
                {calendar.definition} Method: {calendar.method}
              </p>
              <p data-testid="calendar-summary">
                {calendar.contracts_in_window} contract(s) in window ·{" "}
                {calendar.current_quarter_events.length} date(s) this quarter (from{" "}
                {calendar.quarter_start}) · {calendar.needs_attention.length} notice deadline(s)
                inside 90 days with no decision
              </p>
              {calendar.timeline.map((month) => (
                <div key={month.month} data-testid={`calendar-month-${month.month}`}>
                  <h3 className="font-medium">{month.month}</h3>
                  <ul>
                    {month.events.map((e) => (
                      <li key={`${e.contract_id}-${e.kind}`}
                          data-testid={`calendar-event-${e.number}-${e.kind}`}
                          className={e.kind === "notice_deadline" && !e.decided
                            ? "text-red-800 dark:text-red-300" : ""}>
                        {e.date} · {e.kind === "notice_deadline" ? "notice deadline" : "term end"}{" "}
                        · {e.number} {e.vendor_name} · {e.state.replace(/_/g, " ")}
                        {e.in_current_quarter && " · this quarter"}
                        {e.decided && " · decided"}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {calendar.timeline.length === 0 && (
                <p className="text-neutral-700" data-testid="calendar-empty">
                  No contract dates fall inside this window.
                </p>
              )}
            </div>
          )}
        </section>

        <section className="space-y-2" data-testid="obligation-reminders">
          <h2 className="text-lg font-medium">Obligation reminders</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="reminders-load"
                    onClick={() => apiFetch("/contracts/obligation-reminders")
                      .then((r) => (r.ok ? r.json() : null)).then(setReminders)}>
              Preview reminders
            </Button>
            {canWrite && (
              <Button size="sm" variant="outline" data-testid="reminders-send"
                      onClick={() => post("/contracts/obligation-reminders/send", {},
                                           "Obligation reminders").then(setReminders)}>
                Send now
              </Button>
            )}
          </div>
          {reminders && (
            <div className="border rounded-md p-4 space-y-1 text-sm" data-testid="reminders-body">
              <p className="text-neutral-700 dark:text-neutral-300"
                 data-testid="reminders-method">
                {reminders.method}. Email channel: {reminders.email_channel.status}
                {reminders.email_channel.non_production_redirect &&
                  ` (non-production sends go to ${reminders.email_channel.non_production_redirect})`}
              </p>
              <ul>
                {reminders.reminders.map((r) => (
                  <li key={r.obligation_id} data-testid={`reminder-${r.obligation_id}`}>
                    {r.stage} · {r.title} · {r.contract_number} {r.vendor_name} · due{" "}
                    {r.due_date} · {r.owner_email}
                  </li>
                ))}
                {reminders.reminders.length === 0 && (
                  <li className="text-neutral-700" data-testid="reminders-empty">
                    Nothing is due inside {reminders.lead_days} days.
                  </li>
                )}
              </ul>
            </div>
          )}
        </section>

        <section className="space-y-2" data-testid="vendor-compare">
          <h2 className="text-lg font-medium">Compare two vendors</h2>
          <div className="flex flex-wrap gap-2 items-end">
            {["a", "b"].map((side) => (
              <label key={side} className="text-sm">
                Vendor {side.toUpperCase()}
                <select className="block border rounded-md px-2 py-2 text-sm bg-background"
                        data-testid={`compare-${side}`} value={compare[side]}
                        onChange={(e) => setCompare({ ...compare, [side]: e.target.value })}>
                  <option value="">Select…</option>
                  {[...vendors].sort((x, y) => x.name.localeCompare(y.name)).map((v) => (
                    <option key={v.vendor_key} value={v.vendor_key}>{v.name}</option>
                  ))}
                </select>
              </label>
            ))}
            <Button size="sm" variant="outline" data-testid="compare-run"
                    onClick={() => apiFetch(
                      `/vendors/compare?a=${compare.a}&b=${compare.b}`).then(async (r) => {
                        if (!r.ok) { setStatus(`Comparison failed — ${JSON.stringify((await r.json()).detail)}`); return; }
                        setCompare({ ...compare, result: await r.json() });
                      })}>
              Compare
            </Button>
          </div>
          {compare.result && (
            <div className="border rounded-md p-4 space-y-2 text-sm" data-testid="compare-body">
              <p className="text-neutral-700 dark:text-neutral-300" data-testid="compare-note">
                {compare.result.note}
              </p>
              <p data-testid="compare-leads">
                Leads: {compare.result.vendors[0].name} {compare.result.leads.a} ·{" "}
                {compare.result.vendors[1].name} {compare.result.leads.b} · tied{" "}
                {compare.result.leads.tied}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border rounded-md">
                  <caption className="sr-only">Two vendors compared measure by measure</caption>
                  <thead>
                    <tr className="text-left border-b">
                      <th scope="col" className="px-3 py-2">Measure</th>
                      <th scope="col" className="px-3 py-2">{compare.result.vendors[0].name}</th>
                      <th scope="col" className="px-3 py-2">{compare.result.vendors[1].name}</th>
                      <th scope="col" className="px-3 py-2">Leads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compare.result.rows.map((row) => (
                      <tr key={row.metric} className="border-b last:border-0"
                          data-testid={`compare-row-${row.metric.replace(/[^a-z]/gi, "-")}`}>
                        <th scope="row" className="px-3 py-2 text-left font-medium">
                          {row.metric}
                          <span className="block text-xs text-neutral-700 dark:text-neutral-300">
                            {row.method} ({row.better} is better)
                            {row.sample && ` · sample ${row.sample[0]} vs ${row.sample[1]}`}
                          </span>
                        </th>
                        <td className="px-3 py-2">{row.a === null ? "—" : row.a}</td>
                        <td className="px-3 py-2">{row.b === null ? "—" : row.b}</td>
                        <td className="px-3 py-2">
                          {row.leads === "a" ? compare.result.vendors[0].name
                            : row.leads === "b" ? compare.result.vendors[1].name : "tied"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {compare.result.financials_withheld && (
                <p className="text-amber-800" data-testid="compare-withheld">
                  {compare.result.financials_withheld}
                </p>
              )}
            </div>
          )}
        </section>

        <section className="space-y-2" data-testid="credit-dashboard">
          <h2 className="text-lg font-medium">Credits owed across every vendor</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <Button size="sm" variant="outline" data-testid="dashboard-load"
                    onClick={() => apiFetch(
                      `/contracts/credit-dashboard?quarter_only=${quarterOnly}`)
                      .then((r) => (r.ok ? r.json() : null)).then(setDashboard)}>
              Show credit dashboard
            </Button>
            <label className="text-sm flex items-center gap-1">
              <input type="checkbox" data-testid="dashboard-quarter"
                     checked={quarterOnly}
                     onChange={(e) => setQuarterOnly(e.target.checked)} />
              This quarter only
            </label>
          </div>
          {dashboard && (
            <div className="border rounded-md p-4 space-y-2 text-sm" data-testid="dashboard-body">
              <p className="text-neutral-700 dark:text-neutral-300" data-testid="dashboard-method">
                {dashboard.definition} Method: {dashboard.method}
              </p>
              <p data-testid="dashboard-totals">
                Owed and unclaimed: {dashboard.totals.owed_unclaimed} · claimed{" "}
                {dashboard.totals.claimed} · recovered {dashboard.totals.recovered} · waived{" "}
                {dashboard.totals.waived} · window {dashboard.window} ·{" "}
                {dashboard.contracts_assessed} contracts with credit terms
              </p>
              {dashboard.stale_unclaimed.length > 0 && (
                <p className="text-destructive" data-testid="dashboard-stale">
                  {dashboard.stale_unclaimed.length} credit(s) have been owed and unclaimed for
                  more than {dashboard.stale_threshold_days} days — oldest{" "}
                  {dashboard.stale_unclaimed[0].age_days} days ({dashboard.stale_unclaimed[0].number}{" "}
                  {dashboard.stale_unclaimed[0].period})
                </p>
              )}
              <ul className="space-y-2" data-testid="dashboard-vendors">
                {dashboard.vendors.map((v) => (
                  <li key={v.vendor_key} className="border rounded px-3 py-2"
                      data-testid={`dashboard-vendor-${v.vendor_key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span>
                        <span className="font-medium">{v.vendor_name}</span> · owed{" "}
                        {v.currency} {v.owed_unclaimed} · claimed {v.claimed} · recovered{" "}
                        {v.recovered}
                        {v.oldest_unclaimed_period && (
                          <span className="text-neutral-700 dark:text-neutral-300">
                            {" "}· oldest unclaimed {v.oldest_unclaimed_period} (
                            {v.oldest_unclaimed_age_days}d)
                          </span>
                        )}
                      </span>
                      <Button asChild size="sm" variant="ghost"
                              data-testid={`dashboard-pack-${v.vendor_key}`}>
                        <Link to={`/contracts/renewal-pack/${v.vendor_key}`}>Renewal pack</Link>
                      </Button>
                    </div>
                  </li>
                ))}
                {dashboard.vendors.length === 0 && (
                  <li className="text-muted-foreground" data-testid="dashboard-empty">
                    No contract carries credit terms yet, so nothing can be owed.
                  </li>
                )}
              </ul>
            </div>
          )}
        </section>

        <section className="space-y-2" data-testid="spend-panel">
          <h2 className="text-lg font-medium">Committed spend</h2>
          <Button size="sm" variant="outline" data-testid="spend-load"
                  onClick={() => apiFetch("/contracts/spend").then(async (r) => {
                    if (!r.ok) { setSpend({ error: (await r.json()).detail }); return; }
                    setSpend(await r.json());
                  })}>
            Show committed spend
          </Button>
          {spend && spend.error && (
            <p className="text-sm text-amber-800" data-testid="spend-restricted">{spend.error}</p>
          )}
          {spend && !spend.error && (
            <div className="border rounded-md p-4 space-y-2 text-sm" data-testid="spend-body">
              <p className="text-muted-foreground" data-testid="spend-method">
                {spend.definition} Method: {spend.method}. Non-authoritative — {spend.authority_note}
              </p>
              <p data-testid="spend-total">
                Committed total: {spend.currency} {spend.committed_total.toLocaleString()} ·
                unattributed {spend.currency} {spend.unattributed_total.toLocaleString()}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h3 className="font-medium">By provider</h3>
                  <ul data-testid="spend-by-provider">
                    {spend.by_provider.map((p) => (
                      <li key={p.provider_key} data-testid={`spend-provider-${p.provider_key}`}>
                        {p.provider_key}: {spend.currency} {p.annual_value.toLocaleString()}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium">By catalog item</h3>
                  <ul data-testid="spend-by-service">
                    {spend.by_service.slice(0, 12).map((s) => (
                      <li key={s.service_key}>
                        {s.service_key}: {spend.currency} {s.annual_value.toLocaleString()}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {spend.unattributed.length > 0 && (
                <div data-testid="spend-unattributed">
                  <h3 className="font-medium">Unattributed</h3>
                  <ul>
                    {spend.unattributed.map((u) => (
                      <li key={u.number}>
                        {u.number} · {u.vendor_name} · {spend.currency}{" "}
                        {u.annual_value.toLocaleString()} — {u.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>

        {alerts && (
          <section className="space-y-2" data-testid="renewal-alerts">
            <h2 className="text-lg font-medium">Renewal alerts</h2>
            <p className="text-sm text-muted-foreground" data-testid="renewal-alerts-method">
              {alerts.method}. Email channel: {alerts.email_channel.status}
              {alerts.email_channel.non_production_redirect &&
                ` (non-production sends go to ${alerts.email_channel.non_production_redirect})`}
            </p>
            <ul className="text-sm space-y-1">
              {alerts.alerts.map((a) => (
                <li key={a.contract_id} data-testid={`renewal-alert-${a.number}`}>
                  {a.number} · {a.vendor_name} · {a.stage} · notice {a.notice_deadline} ·
                  owner {a.owner_email}
                </li>
              ))}
              {alerts.alerts.length === 0 && (
                <li className="text-muted-foreground" data-testid="renewal-alerts-empty">
                  Nothing is due inside its alert window.
                </li>
              )}
            </ul>
            <Button size="sm" variant="outline" data-testid="renewal-alerts-send"
                    onClick={() => post("/contracts/renewal-alerts/send", {}, "Renewal alerts")}>
              Send alerts now
            </Button>
          </section>
        )}

        <section className="space-y-3" data-testid="vendor-scorecard-panel">
          <h2 className="text-lg font-medium">Vendor scorecard</h2>
          <div className="flex flex-wrap gap-2">
            {[...vendors]
              .sort((a, b) => {
                const has = (v) => (contracts.some((c) => c.vendor_key === v.vendor_key) ? 0 : 1);
                return has(a) - has(b) || a.name.localeCompare(b.name);
              })
              .slice(0, 12)
              .map((v) => (
              <Button key={v.vendor_key} size="sm" variant="outline"
                      data-testid={`scorecard-open-${v.vendor_key}`}
                      onClick={() => apiFetch(`/vendors/${v.vendor_key}/scorecard`)
                        .then((r) => (r.ok ? r.json() : null)).then(setScorecard)}>
                {v.name}
              </Button>
            ))}
          </div>
          {scorecard && (
            <div className="border rounded-md p-4 space-y-2 text-sm" data-testid="vendor-scorecard">
              <h3 className="font-medium" data-testid="scorecard-name">{scorecard.name}</h3>
              <p className="text-muted-foreground" data-testid="scorecard-method">
                {scorecard.method}
              </p>
              <div className="grid gap-3 md:grid-cols-4">
                {[["Outages", scorecard.reliability.outages, "outages"],
                  ["Major incidents", scorecard.reliability.major_incidents, "major"],
                  ["Requests", scorecard.demand.requests_total, "requests"],
                  ["SLA attainment", scorecard.sla.attainment_pct === null
                    ? "—" : `${scorecard.sla.attainment_pct}%`, "sla"],
                  ["Survey responses", scorecard.satisfaction.responses, "surveys"],
                  ["CIs", scorecard.estate.cis, "cis"],
                  ["Assets", scorecard.estate.assets, "assets"],
                  ["BAA", scorecard.risk.baa_status, "baa"]].map(([label, value, id]) => (
                  <div key={id} className="border rounded px-3 py-2"
                       data-testid={`scorecard-${id}`}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-lg font-semibold">{value}</p>
                  </div>
                ))}
              </div>
              <p data-testid="scorecard-commitment">
                {scorecard.commitment
                  ? `Annual commitment ${scorecard.commitment.currency} ${scorecard.commitment.annual_value_total.toLocaleString()} (${scorecard.commitment.note})`
                  : scorecard.commitment_withheld}
              </p>
              <ul className="space-y-1">
                {scorecard.contracts.map((c) => (
                  <li key={c.number} data-testid={`scorecard-contract-${c.number}`}>
                    {c.number} · ends {c.term_end} · {c.renewal.state.replace(/_/g, " ")}
                  </li>
                ))}
              </ul>
              <Button asChild size="sm" variant="outline" data-testid="scorecard-pack-link">
                <Link to={`/contracts/renewal-pack/${scorecard.vendor_key}`}>
                  Open renewal pack
                </Link>
              </Button>
            </div>
          )}
        </section>

        <section className="space-y-2" data-testid="digest-panel">
          <h2 className="text-lg font-medium">Weekly recurrence digest</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="digest-preview"
                    onClick={() => apiFetch("/digests/recurrence")
                      .then((r) => (r.ok ? r.json() : null)).then(setDigest)}>
              Preview digest
            </Button>
            <Button size="sm" variant="outline" data-testid="digest-send"
                    onClick={() => post("/digests/recurrence/send", {}, "Digest")
                      .then(setDigest)}>
              Send now
            </Button>
          </div>
          {digest && (
            <div className="border rounded-md p-4 space-y-2 text-sm" data-testid="digest-preview-body">
              <p className="text-muted-foreground" data-testid="digest-schedule">
                {digest.schedule} · {digest.definition} · email channel{" "}
                {digest.email_channel.status}
              </p>
              {digest.deliveries.map((d) => (
                <div key={d.location_key || "unassigned"} className="border rounded px-3 py-2"
                     data-testid={`digest-site-${d.location_key || "unassigned"}`}>
                  <p className="font-medium">{d.subject}</p>
                  <ul className="text-muted-foreground">
                    {d.rendered.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                  <p className="text-xs mt-1" data-testid={`digest-recipients-${d.location_key || "unassigned"}`}>
                    Recipients: {d.recipients.map((r) => `${r.email} (${r.source})`).join(", ")
                      || "none resolved"}
                  </p>
                </div>
              ))}
              {digest.deliveries.length === 0 && (
                <p className="text-muted-foreground" data-testid="digest-empty">
                  No recurring work to report for any site.
                </p>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
